import { PROMPT_STRATEGIES } from '../config/componentConfig';

/**
 * 提示词构建器
 * 负责根据不同策略构建最终的提示词
 */
export class PromptBuilder {
  constructor(config, componentValues, components = []) {
    this.config = config;
    this.componentValues = componentValues;
    this.components = components;
  }

  /**
   * 构建最终提示词
   * @returns {string} 构建后的提示词
   */
  build() {
    if (!this.config) return '';

    const { strategy, template, prefix, suffix, separator = ' ' } = this.config;

    switch (strategy) {
      case PROMPT_STRATEGIES.TEMPLATE_BASED:
        return this.buildTemplateBasedPrompt(template);
      
      case PROMPT_STRATEGIES.CONCATENATION:
        return this.buildConcatenationPrompt(prefix, suffix, separator);
      
      case PROMPT_STRATEGIES.PREFIX_ONLY:
      case PROMPT_STRATEGIES.PREFIX:
        return this.buildPrefixOnlyPrompt(prefix);
      
      case PROMPT_STRATEGIES.CUSTOM:
        if (typeof this.config.customHandler === 'function') {
          try {
            return this.config.customHandler(this.componentValues, this.components) || '';
          } catch {
            return this.buildCustomPrompt();
          }
        }
        return this.buildCustomPrompt();
      
      default:
        return this.buildDefaultPrompt(separator);
    }
  }

  /**
   * 基于模板的提示词构建
   * 支持占位符替换，如 {componentId}
   */
  buildTemplateBasedPrompt(template) {
    if (!template) return '';

    let result = template;
    
    // 替换模板中的占位符
    Object.keys(this.componentValues).forEach(componentId => {
      const value = this.componentValues[componentId];
      if (value && typeof value === 'string') {
        // 支持多种占位符格式
        const patterns = [
          new RegExp(`\\{${componentId}\\}`, 'g'),           // {componentId}
          new RegExp(`\\{\\{${componentId}\\}\\}`, 'g'),     // {{componentId}}
          new RegExp(`\\$\\{${componentId}\\}`, 'g'),        // ${componentId}
          new RegExp(`%${componentId}%`, 'g')                // %componentId%
        ];
        
        patterns.forEach(pattern => {
          result = result.replace(pattern, value);
        });
      }
    });

    // 处理组件的 promptTemplate 配置
    if (this.components) {
      this.components.forEach(component => {
        const { id, promptTemplate } = component;
        const value = this.componentValues[id];
        
        // 如果组件有值且配置了 promptTemplate，则应用模板
        if (value && promptTemplate) {
          let hasValidValue = false;
          
          // 检查不同类型的值
          if (typeof value === 'string' && value.trim()) {
            hasValidValue = true;
          } else if (Array.isArray(value) && value.length > 0) {
            hasValidValue = true;
          } else if (value && typeof value === 'object') {
            hasValidValue = true;
          }
          
          if (hasValidValue) {
            // 如果 promptTemplate 包含 {value} 占位符，则替换
            if (promptTemplate.includes('{value}')) {
              const displayValue = typeof value === 'string' ? value : 'uploaded content';
              const componentPrompt = promptTemplate.replace(/\{value\}/g, displayValue);
              result += componentPrompt;
            } else {
              // 如果不包含占位符，直接使用模板文本
              result += promptTemplate;
            }
          }
        }
      });
    }

    return result.trim();
  }

  /**
   * 拼接式提示词构建
   * 按顺序拼接前缀、用户输入、后缀
   */
  buildConcatenationPrompt(prefix, suffix, separator) {
    const parts = [];
    
    // 添加前缀
    if (prefix && prefix.trim()) {
      parts.push(prefix.trim());
    }
    
    // 添加用户输入的提示词
    const userPrompts = this.extractUserPrompts();
    if (userPrompts.length > 0) {
      parts.push(...userPrompts);
    }
    
    // 添加后缀
    if (suffix && suffix.trim()) {
      parts.push(suffix.trim());
    }
    
    return parts.join(separator);
  }

  /**
   * 仅前缀模式
   * 只在用户输入前添加前缀
   */
  buildPrefixOnlyPrompt(prefix) {
    const userPrompts = this.extractUserPrompts();
    const userPromptText = userPrompts.join(' ');
    
    if (!userPromptText.trim()) {
      return prefix || '';
    }
    
    return prefix ? `${prefix} ${userPromptText}` : userPromptText;
  }

  /**
   * 自定义构建逻辑
   * 可以在子类中重写此方法实现特殊逻辑
   */
  buildCustomPrompt() {
    const handler = this.config?.customHandler;
    if (typeof handler === 'function') {
      try {
        const result = handler(this.componentValues, this.components, this.config);
        if (typeof result === 'string') return result;
      } catch (e) {
        // ignore handler errors and fallback
      }
    }
    return this.buildConcatenationPrompt(
      this.config?.prefix, 
      this.config?.suffix, 
      (this.config?.separator) || ' '
    );
  }

  /**
   * 默认构建模式
   * 简单拼接所有用户输入
   */
  buildDefaultPrompt(separator) {
    const userPrompts = this.extractUserPrompts();
    return userPrompts.join(separator);
  }

  /**
   * 提取用户输入的提示词
   * @returns {string[]} 用户输入的提示词数组
   */
  extractUserPrompts() {
    const prompts = [];
    
    Object.keys(this.componentValues).forEach(componentId => {
      const value = this.componentValues[componentId];
      if (value && typeof value === 'string' && value.trim()) {
        prompts.push(value.trim());
      }
    });
    
    return prompts;
  }

  /**
   * 验证提示词配置
   * @param {Object} config 提示词配置
   * @returns {Object} 验证结果 {valid: boolean, errors: string[]}
   */
  static validateConfig(config) {
    const errors = [];
    
    if (!config) {
      errors.push('Prompt config is required');
      return { valid: false, errors };
    }

    const { strategy, template } = config;
    
    // 验证策略
    if (strategy && !Object.values(PROMPT_STRATEGIES).includes(strategy)) {
      errors.push(`Invalid prompt strategy: ${strategy}`);
    }
    
    // 验证模板模式的模板
    if (strategy === PROMPT_STRATEGIES.TEMPLATE_BASED && !template) {
      errors.push('Template is required for template-based strategy');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
}

/**
 * 创建提示词构建器实例
 * @param {Object} config - 提示词配置
 * @param {Object} componentValues - 组件值
 * @param {Array} components - 组件配置数组
 * @returns {PromptBuilder} 提示词构建器实例
 */
export function createPromptBuilder(config, componentValues, components = []) {
  return new PromptBuilder(config, componentValues, components);
}

/**
 * 构建提示词的便捷函数
 * @param {Object} config - 提示词配置
 * @param {Object} componentValues - 组件值
 * @param {Array} components - 组件配置数组
 * @returns {string} 构建后的提示词
 */
export function buildPrompt(config, componentValues, components = []) {
  const builder = createPromptBuilder(config, componentValues, components);
  return builder.build();
}

/**
 * 预定义的提示词策略配置
 */
export const PREDEFINED_STRATEGIES = {
  // Logo生成专用策略
  LOGO_GENERATION: {
    strategy: PROMPT_STRATEGIES.PREFIX_ONLY,
    prefix: "You are a professional logo designer. Create a logo based on the following description. The logo should be clean, modern, and suitable for business use. Description:"
  },
  
  // 图片编辑专用策略
  IMAGE_EDITING: {
    strategy: PROMPT_STRATEGIES.PREFIX_ONLY,
    prefix: "You are a professional image editor. Your task is to edit the uploaded image based on the user's prompt. Make sure to preserve the characteristics described by the prompt with the highest fidelity. The prompt is:"
  },
  
  // 艺术风格转换策略
  STYLE_TRANSFER: {
    strategy: PROMPT_STRATEGIES.TEMPLATE_BASED,
    template: "Transform the image into {style} style. {userPrompt}. Maintain the original composition while applying the artistic style."
  },
  
  // 通用文生图策略
  TEXT_TO_IMAGE: {
    strategy: PROMPT_STRATEGIES.CONCATENATION,
    prefix: "Create a high-quality image:",
    suffix: "The image should be detailed, well-composed, and visually appealing.",
    separator: " "
  }
};

export default PromptBuilder;
